<template>
    <div>
        <div class="designer-menu" :class="{ 'timer-trigger': automation.options.trigger === 'timer' }">
            <div class="btnGroup">
                <SupsisButton
                    :primary="selectedTab === 'designer'"
                    :ghost="selectedTab !== 'designer'"
                    @click="() => toggleSelectedTab('designer')"
                >
                    <i class="material-icons">draw</i>
                    {{ $t("message.designer") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'target-contacts'"
                    :ghost="selectedTab !== 'target-contacts'"
                    @click="() => toggleSelectedTab('target-contacts')"
                    v-if="automation.options.trigger === 'timer'"
                >
                    <i class="material-icons">track_changes</i>
                    {{ $t("message.targetContacts") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'scheduler'"
                    :ghost="selectedTab !== 'scheduler'"
                    @click="() => toggleSelectedTab('scheduler')"
                    v-if="automation.options.trigger === 'timer'"
                >
                    <i class="material-icons">schedule</i>
                    {{ $t("message.scheduler") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'webhook'"
                    :ghost="selectedTab !== 'webhook'"
                    @click="() => toggleSelectedTab('webhook')"
                    v-if="automation.options.trigger === 'webhook'"
                >
                    <i class="material-icons">webhook</i>
                    Webhook
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'Plan & Tasks'"
                    :ghost="selectedTab !== 'plan-tasks'"
                    @click="() => toggleSelectedTab('plan-tasks')"
                >
                    <i class="material-icons">dynamic_feed</i>
                    {{ $t("message.planAndTask") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'insights'"
                    :ghost="selectedTab !== 'insights'"
                    @click="() => toggleSelectedTab('insights')"
                >
                    <i class="material-icons">insights</i>
                    {{ $t("message.insights") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'prefilters'"
                    :ghost="selectedTab !== 'prefilters'"
                    @click="() => toggleSelectedTab('prefilters')"
                    v-if="automation.options.trigger === 'event' && isPreFilterSupported"
                >
                    <i class="material-icons">filter_alt</i>
                    {{ $t("automation.preFilters") }}
                </SupsisButton>
                <SupsisButton
                    :primary="selectedTab === 'message-filters'"
                    :ghost="selectedTab !== 'message-filters'"
                    @click="() => toggleSelectedTab('message-filters')"
                    v-if="isMessageMatchedAutomation"
                >
                    <i class="material-icons">mail</i>
                    Mesaj Filtresi
                </SupsisButton>
                <SupsisButton v-if="automation.reference && automation.reference.table" @click="goToTable">
                    <i class="material-icons">table_view</i>
                    Go To Table
                </SupsisButton>
            </div>
        </div>

        <div class="side-bar" v-if="selectedTab" :class="{ 'timer-trigger': automation.options.trigger === 'timer' }">
            <AutomationScheduler
                v-if="selectedTab === 'scheduler'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />

            <AutomationContactSelector
                v-if="selectedTab === 'target-contacts'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />

            <AutomationWebhookDetails
                v-if="selectedTab === 'webhook'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />

            <AutomationTaskHistory
                v-if="selectedTab === 'plan-tasks'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
                :default-automation-id="automation._id"
            />

            <AutomationInsights
                v-if="selectedTab === 'insights'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />

            <AutomationPreFilters
                v-if="selectedTab === 'prefilters'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />

            <AutomationMessageFilters
                v-if="selectedTab === 'message-filters'"
                @close="() => toggleSelectedTab('designer')"
                :automation="automation"
                @updateAutomation="$emit('updateAutomation', $event)"
            />
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../../utils/log2";
import { mapActions, mapGetters, mapState } from "vuex";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import AutomationScheduler from "../../toolbar/AutomationScheduler.vue";
import AutomationContactSelector from "../../toolbar/AutomationContactSelector.vue";
import AutomationWebhookDetails from "../../toolbar/AutomationWebhookDetails.vue";
import AutomationTaskHistory from "../../../automations/AutomationTaskHistory.vue";
import AutomationInsights from "../../toolbar/AutomationInsights.vue";
import AutomationPreFilters from "../../toolbar/AutomationPreFilters.vue";
import SYSTEM_PUBSUB from "../../../../../../../utils/systemPubSub";
import _ from "lodash";
import AutomationMessageFilters from "../toolbar/AutomationMessageFilters.vue";

const log = log2("DesignerMenu");

export default {
    name: "DesignerMenu",
    props: ["automation"],
    components: {
        AutomationTaskHistory,
        AutomationWebhookDetails,
        AutomationScheduler,
        AutomationContactSelector,
        AutomationInsights,
        AutomationPreFilters,
        AutomationMessageFilters,
        SupsisButton,
    },
    computed: {
        isPreFilterSupported() {
            const eventType = _.get(this.automation, "options.event.type", "");
            return eventType.startsWith("chat.") || eventType.startsWith("contact.") || eventType.startsWith("message.") || (eventType.startsWith("email.") && eventType !== "email.received" && eventType !== "email.matched");
        },
        isMessageMatchedAutomation() {
            const eventType = _.get(this.automation, "options.event.type", "");
            return eventType === "message.matched";
        },
    },
    data() {
        return {
            selectedTab: "designer",
        };
    },
    methods: {
        ...mapActions({}),
        toggleSelectedTab(index) {
            if (this.selectedTab !== index) this.selectedTab = index;
            else this.selectedTab = "designer";
        },
        goToTable() {
            if (this.automation.reference && this.automation.reference.table) {
                this.$router.push(`/tables/t/${this.automation.reference.table}`);
            }
        },
    },
    watch: {
        selectedTab(val, oldVal) {
            if (val !== "designer") {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.AUTOMATION_CLOSE_TOOLBAR, {});
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.designer-menu {
    position: absolute;
    top: 10px;
    left: calc(50% - 260px);
    right: unset;
    z-index: 999;
    gap: 6px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0px 4px 20px #00000040;
    width: auto;
    min-width: 250px;
    display: flex;
    align-items: center;

    .btnGroup {
        display: flex;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
        background: var(--color-bg-neutral-default);

        .btnGrupItem {
            &.selected {
                background: var(--color-bg-oncomponent-default);
                box-shadow: var(--shadow-interaction-200);
            }
        }

        .supsis-button {
            border: none !important;
            font-size: 14px;
        }
    }

    &.timer-trigger {
        left: unset;
        right: 2rem;
    }
}

.side-bar {
    position: absolute;
    top: 50px;
    z-index: 999;
    border-radius: 8px;
    width: 100%;

    &.timer-trigger {
        top: 7.5rem;
    }
}
</style>
